import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import CustomerStageBadge, { STAGE_CONFIG, CustomerStageKey } from './CustomerStageBadge';
import './CustomerStageAnalytics.css';

// Type for stage counts - uses string keys since data from Firestore may have unknown stages
interface StageCounts {
  [key: string]: number;
}

// Helper to check if a string is a valid CustomerStageKey
function isCustomerStage(stage: string): stage is CustomerStageKey {
  return stage in STAGE_CONFIG;
}

export default function CustomerStageAnalytics() {
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [stageData, setStageData] = useState<StageCounts>({});
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [engagementRate, setEngagementRate] = useState(0);

  const fetchStageAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      // Use backend API which filters out orphaned profiles (no valid Auth)
      const response = await authFetch(`${getApiBaseUrl()}/getConversionFunnel?range=year`);
      if (!response.ok) throw new Error('Failed to fetch stage data');
      const result = await response.json();
      if (!result.success || !result.data) throw new Error('API returned failure');

      const distribution: StageCounts = result.data.stageDistribution ?? {};
      const total: number = typeof result.data.summary?.totalProfiles === 'number'
        ? result.data.summary.totalProfiles
        : 0;
      let engagedCount = 0;

      for (const [stage, count] of Object.entries(distribution)) {
        if (stage !== 'visitor') {
          engagedCount += count as number;
        }
      }

      const totalFromDistribution = Object.values(distribution).reduce((sum, value) => sum + value, 0);

      setStageData(distribution);
      setTotalCustomers(totalFromDistribution || total);
      setEngagementRate(totalFromDistribution > 0 ? (engagedCount / totalFromDistribution) * 100 : 0);
    } catch (error) {
      console.error('Error fetching stage analytics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStageAnalytics();
  }, [fetchStageAnalytics]);

  if (loading) {
    return <div className="analytics-loading">{t('customerStage.loading')}</div>;
  }

  // Calculate stage percentages
  const stagePercentages = Object.entries(stageData).map(([stage, count]) => ({
    stage,
    count,
    percentage: totalCustomers > 0 ? (count / totalCustomers) * 100 : 0
  })).sort((a, b) => b.count - a.count);

  return (
    <div className="customer-stage-analytics">
      <div className="analytics-header">
        <h2>{t('customerStage.title')}</h2>
        <button onClick={fetchStageAnalytics} className="refresh-btn">
          {t('customerStage.refresh')}
        </button>
      </div>

      {/* Key Metrics */}
      <div className="analytics-metrics">
        <div className="metric-card">
          <div className="metric-value">{totalCustomers}</div>
          <div className="metric-label">{t('customerStage.totalCustomers')}</div>
        </div>
        
        <div className="metric-card">
          <div className="metric-value">
            {totalCustomers - (stageData['visitor'] || 0) - (stageData['churned'] || 0)}
          </div>
          <div className="metric-label">{t('customerStage.engaged')}</div>
        </div>
        
        <div className="metric-card">
          <div className="metric-value">
            {(stageData['converted'] || 0) + (stageData['loyal'] || 0)}
          </div>
          <div className="metric-label">{t('customerStage.buyers')}</div>
        </div>
        
        <div className="metric-card highlight">
          <div className="metric-value">{engagementRate.toFixed(1)}%</div>
          <div className="metric-label">{t('customerStage.engagementRate')}</div>
        </div>
      </div>

      {/* Stage Distribution */}
      <div className="stage-distribution">
        <h3>{t('customerStage.stageDistribution')}</h3>
        
        <div className="stage-chart">
          {stagePercentages.map(({ stage, count, percentage }) => (
            <div key={stage} className="stage-chart-row">
              <div className="stage-info">
                <CustomerStageBadge stage={stage} size="small" />
                <span className="stage-count">{count} {t('customerStage.customers')}</span>
              </div>
              
              <div className="stage-bar-container">
                <div 
                  className="stage-bar" 
                  style={{ 
                    width: `${percentage}%`,
                    backgroundColor: isCustomerStage(stage) ? STAGE_CONFIG[stage].color : '#999'
                  }}
                />
                <span className="stage-percentage">{percentage.toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Engagement Funnel */}
      <div className="funnel-metrics">
        <h3>{t('customerStage.engagementFunnel')}</h3>
        <div className="funnel-steps">
          <div className="funnel-step">
            <div className="funnel-label">{t('customerStage.visitors')}</div>
            <div className="funnel-value">{totalCustomers}</div>
          </div>
          <div className="funnel-arrow">→</div>
          
          <div className="funnel-step">
            <div className="funnel-label">{t('customerStage.feedback')}</div>
            <div className="funnel-value">{stageData['feedback-given'] || 0}</div>
          </div>
          <div className="funnel-arrow">→</div>
          
          <div className="funnel-step">
            <div className="funnel-label">{t('customerStage.converted')}</div>
            <div className="funnel-value">{stageData['converted'] || 0}</div>
          </div>
          <div className="funnel-arrow">→</div>
          
          <div className="funnel-step">
            <div className="funnel-label">{t('customerStage.loyal')}</div>
            <div className="funnel-value">{stageData['loyal'] || 0}</div>
          </div>
          <div className="funnel-arrow">→</div>
          
          <div className="funnel-step highlight">
            <div className="funnel-label">{t('customerStage.churned')}</div>
            <div className="funnel-value">{stageData['churned'] || 0}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
